/**
 * Artisan Theme — Main JavaScript
 * Vanilla JS, no dependencies required
 * Covers: Cart Drawer, Mobile Nav, Search, Product Gallery,
 *         Variant Selection, Quantity, Sticky Header, Accordion,
 *         Predictive Search, Toast, Back to Top, AOS
 */

(function () {
  'use strict';

  /* =====================================================
     UTILITIES
  ===================================================== */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function off(el, ev, fn) { if (el) el.removeEventListener(ev, fn); }
  function debounce(fn, ms) {
    let t; return function () { clearTimeout(t); t = setTimeout(() => fn.apply(this, arguments), ms); };
  }
  function formatMoney(cents) {
    const fmt = (window.theme && window.theme.moneyFormat) || '₹{{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/g, amount)
              .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.floor(cents / 100))
              .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
              .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.floor(cents / 100).toString().replace('.', ','));
  }
  function trap(el) {
    const focusable = el.querySelectorAll('a[href],button:not([disabled]),input,textarea,select,[tabindex]:not([tabindex="-1"])');
    const first = focusable[0], last = focusable[focusable.length - 1];
    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault(); (e.shiftKey ? last : first).focus();
      }
    });
  }

  /* =====================================================
     TOAST NOTIFICATIONS
  ===================================================== */
  function showToast(msg, type, duration) {
    const container = $('#toast-container');
    if (!container) return;
    const el = document.createElement('div');
    el.className = 'toast toast--' + (type || 'info');
    el.innerHTML = msg;
    container.appendChild(el);
    setTimeout(function () {
      el.style.opacity = '0';
      el.style.transform = 'translateY(10px)';
      el.style.transition = '0.3s';
      setTimeout(() => el.remove(), 350);
    }, duration || 3000);
  }

  /* =====================================================
     SITE OVERLAY
  ===================================================== */
  const overlay = $('#site-overlay');
  function showOverlay() { overlay && overlay.classList.add('is-active'); }
  function hideOverlay() { overlay && overlay.classList.remove('is-active'); }
  on(overlay, 'click', function () { closeAll(); });

  function closeAll() {
    closeCartDrawer();
    closeMobileNav();
    closeSearch();
  }

  /* =====================================================
     ANNOUNCEMENT BAR
  ===================================================== */
  on($('.announcement-bar__close'), 'click', function () {
    var bar = $('.announcement-bar');
    if (bar) {
      // Avoid forced reflow - use CSS class instead of offsetHeight
      bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease';
      bar.style.maxHeight = '0';
      bar.style.opacity = '0';
      bar.style.overflow = 'hidden';
      bar.style.paddingTop = '0';
      bar.style.paddingBottom = '0';
      setTimeout(function() { bar.classList.add('is-hidden'); }, 350);
      sessionStorage.setItem('announcementClosed', '1');
    }
  });
  if (sessionStorage.getItem('announcementClosed')) {
    var bar = $('.announcement-bar');
    if (bar) bar.classList.add('is-hidden');
  }

  /* =====================================================
     STICKY HEADER
  ===================================================== */
  const header = $('.site-header');
  let lastScroll = 0;
  if (header) {
    window.addEventListener('scroll', debounce(function () {
      const scroll = window.scrollY;
      header.classList.toggle('is-sticky', scroll > 10);
      if (scroll > 300 && scroll > lastScroll) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastScroll = scroll;
    }, 50), { passive: true });
  }

  /* =====================================================
     MOBILE NAVIGATION
  ===================================================== */
  const mobileNav = $('.mobile-nav');
  function openMobileNav() {
    mobileNav && mobileNav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    showOverlay();
  }
  function closeMobileNav() {
    mobileNav && mobileNav.classList.remove('is-open');
    document.body.style.overflow = '';
    hideOverlay();
  }
  on($('.header__menu-btn'), 'click', openMobileNav);
  on($('.mobile-nav__close'), 'click', closeMobileNav);

  // Submenu toggles
  $$('.mobile-nav__toggle').forEach(function (btn) {
    on(btn, 'click', function () {
      const sub = this.closest('.mobile-nav__item').querySelector('.mobile-nav__sub');
      const icon = this.querySelector('svg');
      if (sub) {
        sub.classList.toggle('is-open');
        if (icon) icon.style.transform = sub.classList.contains('is-open') ? 'rotate(180deg)' : '';
      }
    });
  });

  /* =====================================================
     SEARCH OVERLAY
  ===================================================== */
  const searchOverlay = $('.search-overlay');
  const searchInput = searchOverlay && searchOverlay.querySelector('.search-overlay__input');

  function openSearch() {
    searchOverlay && searchOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    showOverlay();
    setTimeout(() => searchInput && searchInput.focus(), 100);
  }
  function closeSearch() {
    searchOverlay && searchOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    hideOverlay();
  }
  $$('.header__search-btn').forEach(btn => on(btn, 'click', openSearch));
  on($('.search-overlay__close'), 'click', closeSearch);
  on(document, 'keydown', function (e) { if (e.key === 'Escape') closeAll(); });

  /* Predictive Search */
  if (searchInput && window.theme && window.theme.settings && window.theme.settings.predictiveSearch) {
    const resultsBox = $('.predictive-results');
    const debouncedSearch = debounce(function () {
      const q = searchInput.value.trim();
      if (q.length < 2) { if (resultsBox) resultsBox.innerHTML = ''; return; }
      const limit = (window.theme.settings.predictiveSearchLimit) || 6;
      fetch(window.theme.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=' + limit + '&section_id=predictive-search')
        .then(r => r.text())
        .then(function (html) {
          if (!resultsBox) return;
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const results = doc.querySelector('#shopify-section-predictive-search');
          resultsBox.innerHTML = results ? results.innerHTML : '';
        })
        .catch(() => {});
    }, 300);
    on(searchInput, 'input', debouncedSearch);
  }

  /* =====================================================
     CART DRAWER
  ===================================================== */
  const cartDrawer = $('.cart-drawer');
  let cartDrawerReturnFocus = null;

  function openCartDrawer(returnFocusEl) {
    cartDrawerReturnFocus = returnFocusEl || document.activeElement;
    cartDrawer && cartDrawer.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    if (cartDrawer) {
      cartDrawer.removeAttribute('inert');
      cartDrawer.setAttribute('aria-hidden', 'false');
    }
    document.body.style.overflow = 'hidden';
    showOverlay();
    if (cartDrawer) trap(cartDrawer);
    if (cartDrawer) {
      const closeButton = cartDrawer.querySelector('.cart-drawer__close');
      if (closeButton) requestAnimationFrame(() => closeButton.focus());
    }
  }
  function closeCartDrawer() {
    cartDrawer && cartDrawer.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    if (cartDrawer) {
      cartDrawer.setAttribute('aria-hidden', 'true');
      cartDrawer.setAttribute('inert', '');
    }
    document.body.style.overflow = '';
    hideOverlay();

    if (cartDrawerReturnFocus && typeof cartDrawerReturnFocus.focus === 'function') {
      requestAnimationFrame(() => cartDrawerReturnFocus.focus());
    }
    cartDrawerReturnFocus = null;
  }
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;

  on($('.cart-drawer__close'), 'click', closeCartDrawer);
  $$('[data-cart-toggle]').forEach(btn => on(btn, 'click', (event) => {
    event.preventDefault();
    openCartDrawer(btn);
  }));

  /* Cart AJAX helpers */
  function cartFetch(url, body) {
    const endpoint = /\.js($|\?)/.test(url) ? url : (url + '.js');
    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(async function (r) {
      const raw = await r.text();
      if (!raw) return {};
      try {
        return JSON.parse(raw);
      } catch (e) {
        // Some storefront/app setups can return HTML even on success.
        if (r.ok) return { ok: true };
        throw new Error('Cart request failed');
      }
    });
  }

  function getCart() {
    return fetch(window.theme.routes.cart + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.json());
  }

  function updateCartUI(cart) {
    // Update item count badges
    const counts = $$('.header__cart-count, .etsy-cart-count');
    counts.forEach(function (el) {
      el.textContent = cart.item_count;
      el.classList.toggle('is-empty', cart.item_count === 0);
    });

    // Update cart drawer header count
    const drawerCount = $('.cart-drawer__count');
    if (drawerCount) {
      drawerCount.textContent = cart.item_count > 0
        ? '· ' + cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items')
        : '';
    }

    // Update shipping bar
    updateShippingBar(cart.total_price);

    // Re-render cart items
    renderCartItems(cart);

    // Update subtotal
    const subtotalEl = $('.cart-drawer__subtotal-price');
    if (subtotalEl) subtotalEl.innerHTML = formatMoney(cart.total_price);

    // Keep checkout footer in sync with AJAX cart state
    const drawerFooter = $('.cart-drawer__footer');
    if (drawerFooter) drawerFooter.hidden = cart.item_count === 0;

    // Cart page subtotal
    const pageSubtotal = $('#cart-subtotal');
    if (pageSubtotal) pageSubtotal.innerHTML = formatMoney(cart.total_price);
  }

  function updateShippingBar(totalPrice) {
    const threshold = parseInt((window.theme && window.theme.settings && window.theme.settings.freeShippingThreshold) || '49900');
    const fill = $('.shipping-bar__fill');
    const text = $('.shipping-bar__text');
    if (!fill) return;
    const pct = Math.min((totalPrice / threshold) * 100, 100);
    fill.style.width = pct + '%';
    if (text) {
      if (totalPrice >= threshold) {
        text.innerHTML = '<strong>🎉 ' + window.theme.strings.freeShipping + '</strong>';
      } else {
        const remaining = threshold - totalPrice;
        text.innerHTML = 'Add <strong>' + formatMoney(remaining) + '</strong> more for free shipping!';
      }
    }
  }

  function renderCartItems(cart) {
    const container = $('.cart-drawer__items');
    if (!container) return;

    if (cart.item_count === 0) {
      container.innerHTML = '<div class="cart-drawer__empty"><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg><p class="cart-drawer__empty-title">' + window.theme.strings.cartEmpty + '</p><a href="/collections/all" class="btn btn--primary btn--sm" onclick="closeCartDrawer()">Shop Now</a></div>';
      return;
    }

    let html = '';
    cart.items.forEach(function (item) {
      const imgSrc = item.image ? item.image.replace('.jpg', '_200x200.jpg').replace('.png', '_200x200.png').replace('.webp', '_200x200.webp') : '';
      const comparePrice = item.original_price > item.price ? '<s>' + formatMoney(item.original_price) + '</s> ' : '';
      html += '<div class="cart-item" data-key="' + item.key + '">' +
        '<a href="' + item.url + '" class="cart-item__img">' + (imgSrc ? '<img src="' + imgSrc + '" alt="' + item.product_title + '" loading="lazy">' : '') + '</a>' +
        '<div class="cart-item__info">' +
          '<div class="cart-item__vendor">' + (item.vendor || '') + '</div>' +
          '<a href="' + item.url + '" class="cart-item__title">' + item.product_title + '</a>' +
          (item.variant_title && item.variant_title !== 'Default Title' ? '<div class="cart-item__variant">' + item.variant_title + '</div>' : '') +
          '<div class="cart-item__bottom">' +
            '<div class="cart-qty">' +
              '<button class="cart-qty__btn cart-qty__minus" data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease">−</button>' +
              '<span class="cart-qty__num">' + item.quantity + '</span>' +
              '<button class="cart-qty__btn cart-qty__plus" data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase">+</button>' +
            '</div>' +
            '<div class="cart-item__price">' + comparePrice + formatMoney(item.line_price) + '</div>' +
          '</div>' +
          '<button class="cart-item__remove" data-key="' + item.key + '">' + (window.theme.strings.remove || 'Remove') + '</button>' +
        '</div>' +
        '</div>';
    });

    container.innerHTML = html;
    bindCartItemEvents();
  }

  function bindCartItemEvents() {
    $$('.cart-qty__btn').forEach(function (btn) {
      on(btn, 'click', function () {
        const key = this.dataset.key;
        const qty = parseInt(this.dataset.qty);
        if (qty < 0) return;
        this.disabled = true;
        cartFetch(window.theme.routes.cartChange, { id: key, quantity: qty })
          .then(updateCartUI)
          .catch(() => window.location.reload());
      });
    });

    $$('.cart-item__remove').forEach(function (btn) {
      on(btn, 'click', function () {
        const key = this.dataset.key;
        const item = this.closest('.cart-item');
        item.style.opacity = '0.4';
        cartFetch(window.theme.routes.cartChange, { id: key, quantity: 0 })
          .then(updateCartUI)
          .catch(() => window.location.reload());
      });
    });
  }

  // Initialize cart item events if drawer has items
  bindCartItemEvents();

  /* =====================================================
     ADD TO CART
  ===================================================== */
  $$('[data-add-to-cart]').forEach(function (form) {
    on(form, 'submit', function (e) {
      e.preventDefault();
      if (form.dataset.atcSubmitting === 'true') return;
      form.dataset.atcSubmitting = 'true';
      const btn = form.querySelector('[data-atc-btn]') || form.querySelector('[type=submit]');
      const variantId = form.querySelector('[name=id]').value;
      const qty = parseInt((form.querySelector('[name=quantity]') || {}).value || 1);

      if (btn) { btn.disabled = true; btn.textContent = window.theme.strings.addingToCart || 'Adding...'; }

      cartFetch(window.theme.routes.cartAdd, { id: variantId, quantity: qty })
        .then(function (item) {
          if (item && item.status && Number(item.status) >= 400) {
            throw new Error(item.description || item.message || 'Error');
          }
          return getCart().then(function (cart) {
            updateCartUI(cart);
            showToast('✓ Added to cart', 'success');
          }).catch(function () {
            // If cart refresh fails but add succeeds, avoid false-negative error toast.
            showToast('✓ Added to cart', 'success');
          });
        })
        .catch(function (err) {
          showToast((err && err.message) ? err.message : 'Could not add item. Please try again.', 'error');
        })
        .finally(function () {
          if (btn) { btn.disabled = false; btn.textContent = window.theme.strings.addToCart || 'Add to Cart'; }
          form.dataset.atcSubmitting = 'false';
        });
    });
  });

  /* =====================================================
     QUICK ADD (product cards)
  ===================================================== */
  $$('[data-quick-add]').forEach(function (btn) {
    on(btn, 'click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const variantId = this.dataset.variantId;
      if (!variantId) return;
      btn.disabled = true;
      cartFetch(window.theme.routes.cartAdd, { id: variantId, quantity: 1 })
        .then(function () {
          getCart().then(function (cart) {
            updateCartUI(cart);
            showToast('✓ Added to cart', 'success');
            btn.disabled = false;
          });
        })
        .catch(function () {
          showToast('Could not add item.', 'error');
          btn.disabled = false;
        });
    });
  });

  /* =====================================================
     PRODUCT GALLERY
  ===================================================== */
  const galleryMain = $('.product-gallery__main');
  const galleryThumbs = $$('.product-gallery__thumb');

  galleryThumbs.forEach(function (thumb, i) {
    on(thumb, 'click', function () {
      const src = this.dataset.src;
      const mainImg = galleryMain && galleryMain.querySelector('img');
      if (mainImg && src) {
        mainImg.style.opacity = '0.5';
        setTimeout(function () {
          mainImg.src = src;
          mainImg.style.opacity = '1';
        }, 150);
      }
      galleryThumbs.forEach(t => t.classList.remove('is-active'));
      this.classList.add('is-active');
    });
  });

  /* =====================================================
     VARIANT SELECTOR
  ===================================================== */
  const productPage = $('[data-product-page]');
  if (productPage) {
    const variantForm = productPage.querySelector('[data-add-to-cart]');
    const variantIdInput = variantForm && variantForm.querySelector('[name=id]');
    const priceEl = productPage.querySelector('[data-price]');
    const comparePriceEl = productPage.querySelector('[data-compare-price]');
    const atcBtn = variantForm && variantForm.querySelector('[data-atc-btn]');
    const buyNowBtn = productPage.querySelector('#buy-now-btn');
    const stickyAtcBtn = $('.sticky-atc__btn');
    const galleryMainImg = productPage.querySelector('#gallery-main-img');

    let productData;
    try { productData = JSON.parse(($('[data-product-json]') || {}).textContent || 'null'); } catch(e) {}

    function getSelectedVariant() {
      if (!productData) return null;
      const selected = {};
      $$('select[data-option-name]', productPage).forEach(function (sel) {
        selected[sel.dataset.optionName] = sel.value;
      });
      return productData.variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return opt === selected['option' + (i + 1)];
        });
      });
    }

    function updateVariantUI(variant) {
      if (!variant) return;
      if (variantIdInput) variantIdInput.value = variant.id;

      if (priceEl) priceEl.innerHTML = formatMoney(variant.price);
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.innerHTML = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      var unavailable = !variant.available;

      if (atcBtn) {
        atcBtn.disabled = unavailable;
        atcBtn.textContent = unavailable ? (window.theme.strings.soldOut || 'Sold Out') : (window.theme.strings.addToCart || 'Add to Cart');
      }
      if (buyNowBtn) {
        buyNowBtn.disabled = unavailable;
        buyNowBtn.textContent = unavailable ? 'Coming Soon' : (window.theme.strings.buyNow || 'Buy Now');
        buyNowBtn.style.opacity = unavailable ? '0.5' : '1';
      }
      if (stickyAtcBtn) {
        stickyAtcBtn.disabled = unavailable;
        stickyAtcBtn.textContent = unavailable ? (window.theme.strings.soldOut || 'Sold Out') : (window.theme.strings.addToCart || 'Add to Cart');
      }

      // Update gallery image if variant has an image
      if (galleryMainImg && variant.featured_image && variant.featured_image.src) {
        galleryMainImg.src = variant.featured_image.src;
        var thumbs = $$('.product-gallery__thumb', productPage);
        thumbs.forEach(function(t) { t.classList.remove('is-active'); });
        thumbs.forEach(function(t) {
          if (t.dataset.src && variant.featured_image.src.indexOf(t.dataset.src.split('?')[0].replace(/_\d+x\d*/, '')) > -1) {
            t.classList.add('is-active');
          }
        });
      }

      // Update selected label text
      var optionLabels = $$('.option-group__selected', productPage);
      variant.options.forEach(function(opt, i) {
        if (optionLabels[i]) optionLabels[i].textContent = opt;
      });

      // Update URL
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Option buttons click handler
    $$('[data-option-btn]', productPage).forEach(function (btn) {
      on(btn, 'click', function () {
        var name = this.dataset.optionName;
        var val = this.dataset.optionValue;
        // Update active state on buttons in same group
        var group = this.closest('.option-group__values');
        if (group) {
          group.querySelectorAll('[data-option-btn]').forEach(function(b) { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
        }
        this.classList.add('is-active');
        this.setAttribute('aria-pressed', 'true');
        // Update hidden select
        var sel = productPage.querySelector('select[data-option-name="' + name + '"]');
        if (sel) { sel.value = val; }
        // Find and apply variant
        var v = getSelectedVariant();
        if (v) updateVariantUI(v);
      });
    });

    // Also handle if selects are used directly
    $$('select[data-option-name]', productPage).forEach(function (sel) {
      on(sel, 'change', function () {
        var v = getSelectedVariant();
        if (v) updateVariantUI(v);
      });
    });

    // On page load, if URL has ?variant=, select it
    (function() {
      var params = new URLSearchParams(window.location.search);
      var vid = params.get('variant');
      if (vid && productData) {
        var v = productData.variants.find(function(vr) { return String(vr.id) === vid; });
        if (v) {
          v.options.forEach(function(opt, i) {
            var sel = productPage.querySelector('select[data-option-name="option' + (i+1) + '"]');
            if (sel) sel.value = opt;
            var btns = $$('[data-option-name="option' + (i+1) + '"]', productPage);
            btns.forEach(function(b) {
              if (b.tagName === 'BUTTON') {
                b.classList.toggle('is-active', b.dataset.optionValue === opt);
                b.setAttribute('aria-pressed', b.dataset.optionValue === opt ? 'true' : 'false');
              }
            });
          });
          updateVariantUI(v);
        }
      }
    })();
  }

  /* =====================================================
     QUANTITY SELECTOR
  ===================================================== */
  $$('.quantity-selector').forEach(function (qs) {
    const minus = qs.querySelector('.quantity-selector__btn--minus');
    const plus = qs.querySelector('.quantity-selector__btn--plus');
    const input = qs.querySelector('.quantity-selector__input');
    on(minus, 'click', function () { if (input && parseInt(input.value) > 1) input.value = parseInt(input.value) - 1; });
    on(plus, 'click', function () { if (input) input.value = parseInt(input.value) + 1; });
    on(input, 'change', function () { if (parseInt(this.value) < 1) this.value = 1; });
  });

  /* =====================================================
     STICKY ADD TO CART
  ===================================================== */
  const stickyAtc = $('.sticky-atc');
  const productInfoActions = $('.product-info__actions');
  if (stickyAtc && productInfoActions) {
    const obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        stickyAtc.style.display = entry.isIntersecting ? 'none' : 'block';
      });
    }, { threshold: 0.1 });
    obs.observe(productInfoActions);
  }
  on($('.sticky-atc__btn'), 'click', function () {
    const mainForm = $('[data-add-to-cart]');
    if (mainForm) mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });

  /* =====================================================
     DEFERRED NON-CRITICAL INIT (runs when main thread is idle)
  ===================================================== */
  var deferInit = function () {

  /* =====================================================
     ACCORDION
  ===================================================== */
  $$('.accordion__btn').forEach(function (btn) {
    on(btn, 'click', function (e) {
      e.preventDefault();

      const content = this.nextElementSibling;
      const isOpen = this.getAttribute('aria-expanded') === 'true';
      const accordion = this.closest('.accordion');
      const isProductAccordion = accordion && accordion.hasAttribute('data-product-accordion');

      if (isProductAccordion) {
        this.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (content) content.classList.toggle('is-open', !isOpen);
        return;
      }

      const buttons = accordion ? $$('.accordion__btn', accordion) : $$('.accordion__btn');

      buttons.forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
        const c = b.nextElementSibling;
        if (c) c.classList.remove('is-open');
      });

      if (!isOpen) {
        this.setAttribute('aria-expanded', 'true');
        if (content) content.classList.add('is-open');
      }
    });
  });

  /* =====================================================
     COLLECTION FILTERS (Mobile Drawer)
  ===================================================== */
  const filterToggle = $('.collection-filter-toggle');
  const filterDrawer = $('.filter-drawer-mobile');
  const filterClose = $('.filter-drawer-mobile__close');
  on(filterToggle, 'click', function () {
    filterDrawer && filterDrawer.classList.add('is-open');
    showOverlay();
  });
  on(filterClose, 'click', function () {
    filterDrawer && filterDrawer.classList.remove('is-open');
    hideOverlay();
  });

  // Sort select — merge sort_by into current URL so filter.p.tag etc. stay
  $$('[data-sort-select]').forEach(function (el) {
    on(el, 'change', function () {
      try {
        var next = new URL(this.value, window.location.origin);
        var sortBy = next.searchParams.get('sort_by');
        var u = new URL(window.location.href);
        if (sortBy) u.searchParams.set('sort_by', sortBy);
        else u.searchParams.delete('sort_by');
        u.searchParams.delete('page');
        window.location.href = u.pathname + (u.search || '');
      } catch (e) {
        window.location.href = this.value;
      }
    });
  });


  /* =====================================================
     SCROLL REVEAL (minimal AOS replacement)
  ===================================================== */
  const aosElements = $$('[data-aos]');
  if (aosElements.length) {
    // Use rAF to avoid blocking main thread during initial render
    requestAnimationFrame(function() {
      const revealObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('aos-animate');
            revealObs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
      aosElements.forEach(function(el) { revealObs.observe(el); });
    });
  }

  /* =====================================================
     HEADER SORT (moved from inline script)
  ===================================================== */
  $$('.js-header-collection-sort').forEach(function (sel) {
    on(sel, 'change', function () {
      var base = this.getAttribute('data-sort-base');
      if (base) {
        var u = new URL(base, window.location.origin);
        u.searchParams.set('sort_by', this.value);
        u.searchParams.delete('page');
        window.location.href = u.pathname + (u.search ? u.search : '');
        return;
      }
      var u = new URL(window.location.href);
      u.searchParams.set('sort_by', this.value);
      u.searchParams.delete('page');
      window.location.href = u.pathname + (u.search || '');
    });
  });

/* =====================================================
     SLIDER DRAG SCROLL (moved from inline script)
  ===================================================== */
  $$('.etsy-slider__list').forEach(function (slider) {
    var isDown = false, startX, scrollLeft;
    on(slider, 'mousedown', function (e) {
      isDown = true;
      slider.style.cursor = 'grabbing';
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });
    on(slider, 'mouseleave', function () { isDown = false; slider.style.cursor = ''; });
    on(slider, 'mouseup', function () { isDown = false; slider.style.cursor = ''; });
    on(slider, 'mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault();
      slider.scrollLeft = scrollLeft - ((e.pageX - slider.offsetLeft) - startX) * 2;
    });
  });

  /* =====================================================
     COPY COUPON CODE
  ===================================================== */
  on($('[data-copy-code]'), 'click', function () {
    const code = this.dataset.copyCode;
    navigator.clipboard && navigator.clipboard.writeText(code).then(function () {
      showToast('Coupon code copied!', 'success');
    });
  });

  /* =====================================================
     NEWSLETTER FORM (Shopify Customer)
  ===================================================== */
  const newsletterForm = $('[data-newsletter-form]');
  on(newsletterForm, 'submit', function (e) {
    const email = (this.querySelector('[type=email]') || {}).value;
    if (!email || !email.includes('@')) {
      e.preventDefault();
      showToast('Please enter a valid email.', 'error');
    }
  });

  /* =====================================================
     LAZY LOAD IMAGES (native fallback)
  ===================================================== */
  if ('loading' in HTMLImageElement.prototype === false) {
    $$('img[loading=lazy]').forEach(function (img) {
      img.src = img.dataset.src || img.src;
    });
  }

  /* =====================================================
     MEGA MENU KEYBOARD SUPPORT
  ===================================================== */
  $$('.nav__item').forEach(function (item) {
    const link = item.querySelector('.nav__link');
    const dropdown = item.querySelector('.nav__dropdown, .nav__mega');
    if (!dropdown) return;
    on(link, 'focus', function () { item.classList.add('is-focused'); });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) item.classList.remove('is-focused');
    });
  });

  /* =====================================================
     CART PAGE (separate from drawer)
  ===================================================== */
  $$('[data-cart-remove]').forEach(function (btn) {
    on(btn, 'click', function () {
      const key = this.dataset.cartRemove;
      const row = this.closest('[data-cart-item]');
      if (row) { row.style.opacity = '0.4'; row.style.pointerEvents = 'none'; }
      cartFetch(window.theme.routes.cartChange, { id: key, quantity: 0 })
        .then(function (cart) {
          if (cart.item_count === 0) window.location.reload();
          else window.location.reload();
        });
    });
  });

  $$('[data-cart-qty-btn]').forEach(function (btn) {
    on(btn, 'click', function () {
      const key = this.dataset.key;
      const qty = parseInt(this.dataset.qty);
      if (qty < 0) return;
      this.disabled = true;
      cartFetch(window.theme.routes.cartChange, { id: key, quantity: qty })
        .then(function () { window.location.reload(); });
    });
  });

  /* Expose openCartDrawer globally for section buttons */
  window.theme = window.theme || {};
  window.theme.openCart = openCartDrawer;

  /* Final: init cart count from page data attribute */
  const cartCountInit = document.body.dataset.cartCount;
  if (cartCountInit) {
    $$('.header__cart-count, .etsy-cart-count').forEach(function (el) {
      el.textContent = cartCountInit;
      el.classList.toggle('is-empty', cartCountInit === '0');
    });
  }

  }; // end deferInit
  if ('requestIdleCallback' in window) { requestIdleCallback(deferInit, { timeout: 2000 }); }
  else { setTimeout(deferInit, 50); }

})();

/* JAM PROFESSIONAL NATIVE SHIPPING POLICY */
(function () {
  if (window.location.pathname !== '/policies/shipping-policy') return;

  var policyContainer = document.querySelector('.shopify-policy__container');
  var policyBody = document.querySelector('.shopify-policy__body');
  if (!policyContainer || !policyBody) return;

  var main = policyContainer.parentElement;
  if (!main || main.closest('.jam-shipping-policy')) return;

  var shell = document.createElement('section');
  shell.className = 'jam-shipping-policy';

  var inner = document.createElement('div');
  inner.className = 'container';

  var hero = document.createElement('header');
  hero.className = 'jam-shipping-policy__hero';
  hero.innerHTML =
    '<span class="jam-shipping-policy__eyebrow">Shipping &amp; Delivery</span>' +
    '<h1>Shipping Policy</h1>' +
    '<p>Important information about order processing, delivery timelines, tracking, shipping charges and international customs.</p>';

  var highlights = document.createElement('div');
  highlights.className = 'jam-shipping-policy__highlights';
  highlights.innerHTML =
    '<article class="jam-shipping-policy__highlight">' +
      '<span class="jam-shipping-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3c3 3 4.5 6 4.5 9S15 18 12 21c-3-3-4.5-6-4.5-9S9 6 12 3Z"></path></svg></span>' +
      '<h2>International Shipping</h2><p>Shipping is available to international destinations, subject to serviceability and local regulations.</p>' +
    '</article>' +
    '<article class="jam-shipping-policy__highlight">' +
      '<span class="jam-shipping-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg></span>' +
      '<h2>Order Processing</h2><p>Orders are prepared before dispatch according to the processing information in this policy.</p>' +
    '</article>' +
    '<article class="jam-shipping-policy__highlight">' +
      '<span class="jam-shipping-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M3 6h12v10H3z"></path><path d="M15 9h3l3 3v4h-6z"></path><circle cx="7" cy="18" r="2"></circle><circle cx="18" cy="18" r="2"></circle></svg></span>' +
      '<h2>Shipment Tracking</h2><p>Tracking details are provided after dispatch so customers can follow shipment progress.</p>' +
    '</article>' +
    '<article class="jam-shipping-policy__highlight">' +
      '<span class="jam-shipping-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M4 5h16v14H4z"></path><path d="M8 9h8M8 13h5"></path></svg></span>' +
      '<h2>Customs &amp; Duties</h2><p>International shipments may be subject to destination-country customs duties, taxes or fees.</p>' +
    '</article>';

  var contentWrap = document.createElement('div');
  contentWrap.className = 'jam-shipping-policy__content';

  /* Convert the current plain-text star formatting into semantic headings/lists.
     This changes presentation only; the Shopify policy source remains untouched. */
  var raw = policyBody.innerText
    .split(/\n+/)
    .map(function (line) { return line.trim(); })
    .filter(Boolean);

  if (raw.length) {
    var fragment = document.createDocumentFragment();
    var list = null;

    function flushList() {
      if (list) {
        fragment.appendChild(list);
        list = null;
      }
    }

    raw.forEach(function (line, index) {
      if (/^\*Shipping Policy\*$/i.test(line)) return;

      var heading = line.match(/^\*(\d+\.\s*.+)\*$/);
      if (heading) {
        flushList();
        var h2 = document.createElement('h2');
        h2.textContent = heading[1];
        fragment.appendChild(h2);
        return;
      }

      if (/^\*\s+/.test(line)) {
        if (!list) list = document.createElement('ul');
        var li = document.createElement('li');
        li.textContent = line.replace(/^\*\s+/, '');
        list.appendChild(li);
        return;
      }

      flushList();
      var p = document.createElement('p');
      p.textContent = line.replace(/^\*|\*$/g, '');
      fragment.appendChild(p);
    });

    flushList();
    policyBody.innerHTML = '';
    policyBody.appendChild(fragment);
  }

  contentWrap.appendChild(policyContainer);

  var help = document.createElement('aside');
  help.className = 'jam-shipping-policy__help';
  help.innerHTML =
    '<div><h2>Need help with shipping?</h2><p>For an order-specific question, include your order number when you contact us.</p></div>' +
    '<a href="/pages/contact-us">Contact Us <span aria-hidden="true">&rarr;</span></a>';

  inner.appendChild(hero);
  inner.appendChild(highlights);
  inner.appendChild(contentWrap);
  inner.appendChild(help);
  shell.appendChild(inner);

  main.insertBefore(shell, main.firstChild);
})();

/* JAM PROFESSIONAL NATIVE REFUND POLICY */
(function () {
  if (window.location.pathname !== '/policies/refund-policy') return;

  var policyContainer = document.querySelector('.shopify-policy__container');
  var policyBody = document.querySelector('.shopify-policy__body');
  if (!policyContainer || !policyBody) return;

  var main = policyContainer.parentElement;
  if (!main || main.closest('.jam-refund-policy')) return;

  var shell = document.createElement('section');
  shell.className = 'jam-refund-policy';

  var inner = document.createElement('div');
  inner.className = 'container';

  var hero = document.createElement('header');
  hero.className = 'jam-refund-policy__hero';
  hero.innerHTML =
    '<span class="jam-refund-policy__eyebrow">Returns &amp; Refunds</span>' +
    '<h1>Return &amp; Refund Policy</h1>' +
    '<p>Please review the eligibility, condition requirements, non-returnable items and claim process before requesting a return or refund.</p>';

  var highlights = document.createElement('div');
  highlights.className = 'jam-refund-policy__highlights';
  highlights.innerHTML =
    '<article class="jam-refund-policy__highlight">' +
      '<span class="jam-refund-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg></span>' +
      '<h2>7-Day Window</h2><p>Eligible international returns must be requested within 7 days of delivery.</p>' +
    '</article>' +
    '<article class="jam-refund-policy__highlight">' +
      '<span class="jam-refund-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M4 7h16v12H4z"></path><path d="M8 7V5h8v2"></path><path d="M4 11h16"></path></svg></span>' +
      '<h2>Original Condition</h2><p>Eligible items must meet the condition and packaging requirements stated in the policy.</p>' +
    '</article>' +
    '<article class="jam-refund-policy__highlight">' +
      '<span class="jam-refund-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M5 3h14v18H5z"></path><path d="M8 8h8M8 12h6"></path></svg></span>' +
      '<h2>Proof of Purchase</h2><p>Proof of purchase is required when requesting an eligible return.</p>' +
    '</article>' +
    '<article class="jam-refund-policy__highlight">' +
      '<span class="jam-refund-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M12 3 2 21h20L12 3Z"></path><path d="M12 9v5"></path><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"></circle></svg></span>' +
      '<h2>Damaged or Incorrect Item</h2><p>Contact us within 48 hours of delivery when reporting an eligible damaged, defective or incorrect item.</p>' +
    '</article>';

  var contentWrap = document.createElement('div');
  contentWrap.className = 'jam-refund-policy__content';
  contentWrap.appendChild(policyContainer);

  var note = document.createElement('aside');
  note.className = 'jam-refund-policy__note';
  note.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6"></path><circle cx="12" cy="7" r="1" fill="currentColor" stroke="none"></circle></svg>' +
    '<p>Natural materials can have normal variations in colour, texture or finish. Please read the full policy for the conditions that apply to natural products and non-returnable items.</p>';

  var help = document.createElement('aside');
  help.className = 'jam-refund-policy__help';
  help.innerHTML =
    '<div><h2>Need help with a return?</h2><p>For an order-specific request, include your order number and the information required by the policy.</p></div>' +
    '<a href="/pages/contact-us">Contact Us <span aria-hidden="true">&rarr;</span></a>';

  inner.appendChild(hero);
  inner.appendChild(highlights);
  inner.appendChild(contentWrap);
  inner.appendChild(note);
  inner.appendChild(help);
  shell.appendChild(inner);

  main.insertBefore(shell, main.firstChild);
})();

/* JAM PROFESSIONAL NATIVE PRIVACY POLICY */
(function () {
  if (window.location.pathname !== '/policies/privacy-policy') return;

  var policyContainer = document.querySelector('.shopify-policy__container');
  var policyBody = document.querySelector('.shopify-policy__body');
  if (!policyContainer || !policyBody) return;

  var main = policyContainer.parentElement;
  if (!main || main.closest('.jam-privacy-policy')) return;

  var shell = document.createElement('section');
  shell.className = 'jam-privacy-policy';

  var inner = document.createElement('div');
  inner.className = 'container';

  var hero = document.createElement('header');
  hero.className = 'jam-privacy-policy__hero';
  hero.innerHTML =
    '<span class="jam-privacy-policy__eyebrow">Privacy &amp; Data Protection</span>' +
    '<h1>Privacy Policy</h1>' +
    '<p>Learn what information may be collected, how it may be used, how payment details are handled and what choices may be available to you.</p>';

  var highlights = document.createElement('div');
  highlights.className = 'jam-privacy-policy__highlights';
  highlights.innerHTML =
    '<article class="jam-privacy-policy__highlight">' +
      '<span class="jam-privacy-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M12 3 20 6v6c0 5-3.2 9.1-8 11-4.8-1.9-8-6-8-11V6l8-3Z"></path><path d="m8.5 12 2.2 2.2 4.8-5"></path></svg></span>' +
      '<h2>Personal Information</h2><p>The policy explains the types of personal information that may be collected.</p>' +
    '</article>' +
    '<article class="jam-privacy-policy__highlight">' +
      '<span class="jam-privacy-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="3" y="5" width="18" height="14" rx="2"></rect><path d="M3 10h18"></path></svg></span>' +
      '<h2>Payment Security</h2><p>The policy describes how payment information is handled through payment providers.</p>' +
    '</article>' +
    '<article class="jam-privacy-policy__highlight">' +
      '<span class="jam-privacy-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"></circle><path d="M8 12h8M12 8v8"></path></svg></span>' +
      '<h2>Cookies &amp; Usage</h2><p>Technical and usage information, including cookies, may be covered by this policy.</p>' +
    '</article>' +
    '<article class="jam-privacy-policy__highlight">' +
      '<span class="jam-privacy-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M5 4h14v16H5z"></path><path d="M8 8h8M8 12h6M8 16h5"></path></svg></span>' +
      '<h2>Your Choices</h2><p>Review the full policy for the rights, choices or contact options that apply.</p>' +
    '</article>';

  var contentWrap = document.createElement('div');
  contentWrap.className = 'jam-privacy-policy__content';

  /* Presentation-only cleanup for native policy text.
     Source wording in Shopify Admin is not changed. */
  var raw = policyBody.innerText
    .split(/\n+/)
    .map(function (line) { return line.trim(); })
    .filter(Boolean);

  if (raw.length) {
    var fragment = document.createDocumentFragment();
    var list = null;

    function flushList() {
      if (list) {
        fragment.appendChild(list);
        list = null;
      }
    }

    raw.forEach(function (line) {
      if (/^Privacy policy$/i.test(line)) return;

      if (/^\d+\.\s+/.test(line)) {
        flushList();
        var h2 = document.createElement('h2');
        h2.textContent = line;
        fragment.appendChild(h2);
        return;
      }

      if (/^[a-z]\.\s+/i.test(line)) {
        flushList();
        var h3 = document.createElement('h3');
        h3.textContent = line;
        fragment.appendChild(h3);
        return;
      }

      var listLike = /^(Full name|Billing and shipping address|Email address|Phone number|IP address|Browser type and version|Device type|Pages visited and time spent on the website|Cookies and tracking technologies|To process|To manage|To communicate|To improve|To detect|To comply|To prevent|To provide)/i.test(line);

      if (listLike) {
        if (!list) list = document.createElement('ul');
        var li = document.createElement('li');
        li.textContent = line;
        list.appendChild(li);
        return;
      }

      flushList();
      var p = document.createElement('p');
      p.textContent = line;
      fragment.appendChild(p);
    });

    flushList();
    policyBody.innerHTML = '';
    policyBody.appendChild(fragment);
  }

  contentWrap.appendChild(policyContainer);

  var notice = document.createElement('aside');
  notice.className = 'jam-privacy-policy__notice';
  notice.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6"></path><circle cx="12" cy="7" r="1" fill="currentColor" stroke="none"></circle></svg>' +
    '<p>This page is a presentation of the store privacy policy. The policy wording itself remains controlled by the Shopify policy content.</p>';

  var help = document.createElement('aside');
  help.className = 'jam-privacy-policy__help';
  help.innerHTML =
    '<div><h2>Questions about your privacy?</h2><p>Use the Contact Us page for privacy or personal-information questions.</p></div>' +
    '<a href="/pages/contact-us">Contact Us <span aria-hidden="true">&rarr;</span></a>';

  inner.appendChild(hero);
  inner.appendChild(highlights);
  inner.appendChild(contentWrap);
  inner.appendChild(notice);
  inner.appendChild(help);
  shell.appendChild(inner);

  main.insertBefore(shell, main.firstChild);
})();

/* JAM PROFESSIONAL NATIVE TERMS OF SERVICE */
(function () {
  if (window.location.pathname !== '/policies/terms-of-service') return;

  var policyContainer = document.querySelector('.shopify-policy__container');
  var policyBody = document.querySelector('.shopify-policy__body');
  if (!policyContainer || !policyBody) return;

  var main = policyContainer.parentElement;
  if (!main || main.closest('.jam-terms-policy')) return;

  var shell = document.createElement('section');
  shell.className = 'jam-terms-policy';

  var inner = document.createElement('div');
  inner.className = 'container';

  var hero = document.createElement('header');
  hero.className = 'jam-terms-policy__hero';
  hero.innerHTML =
    '<span class="jam-terms-policy__eyebrow">Legal &amp; Store Terms</span>' +
    '<h1>Terms of Service</h1>' +
    '<p>Please review the terms governing use of this website, product information, orders, pricing, shipping, returns and other store services.</p>';

  var highlights = document.createElement('div');
  highlights.className = 'jam-terms-policy__highlights';
  highlights.innerHTML =
    '<article class="jam-terms-policy__highlight">' +
      '<span class="jam-terms-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M5 4h14v16H5z"></path><path d="M8 8h8M8 12h6"></path></svg></span>' +
      '<h2>Website Use</h2><p>The terms explain the conditions that apply when using the site and its services.</p>' +
    '</article>' +
    '<article class="jam-terms-policy__highlight">' +
      '<span class="jam-terms-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M7 3h10l2 5-7 13L5 8l2-5Z"></path><path d="M5 8h14"></path></svg></span>' +
      '<h2>Product Information</h2><p>Natural and handcrafted products may have normal variations in colour, shape, size or finish.</p>' +
    '</article>' +
    '<article class="jam-terms-policy__highlight">' +
      '<span class="jam-terms-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M4 5h16v14H4z"></path><path d="M4 10h16"></path><path d="M8 15h4"></path></svg></span>' +
      '<h2>Orders &amp; Pricing</h2><p>Review the policy for the conditions relating to pricing, order acceptance and modifications.</p>' +
    '</article>' +
    '<article class="jam-terms-policy__highlight">' +
      '<span class="jam-terms-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M3 6h12v10H3z"></path><path d="M15 9h3l3 3v4h-6z"></path><circle cx="7" cy="18" r="2"></circle><circle cx="18" cy="18" r="2"></circle></svg></span>' +
      '<h2>Shipping &amp; Returns</h2><p>The terms work together with the store shipping and return/refund policies.</p>' +
    '</article>';

  var contentWrap = document.createElement('div');
  contentWrap.className = 'jam-terms-policy__content';

  /* Presentation-only cleanup. Shopify Admin policy wording is not changed. */
  var raw = policyBody.innerText
    .split(/\n+/)
    .map(function (line) { return line.trim(); })
    .filter(Boolean);

  if (raw.length) {
    var fragment = document.createDocumentFragment();
    var list = null;

    function flushList() {
      if (list) {
        fragment.appendChild(list);
        list = null;
      }
    }

    raw.forEach(function (line) {
      if (/^Terms of service$/i.test(line)) return;

      if (/^\d+\.\s+/.test(line)) {
        flushList();
        var h2 = document.createElement('h2');
        h2.textContent = line;
        fragment.appendChild(h2);
        return;
      }

      var listLike = /^(Slight variations|These variations|We strive|Suspected fraud|Incorrect pricing|Issues with payment|You may not|You must not|We reserve|You agree|You may|All prices|Delivery times|International orders)/i.test(line);

      if (listLike) {
        if (!list) list = document.createElement('ul');
        var li = document.createElement('li');
        li.textContent = line;
        list.appendChild(li);
        return;
      }

      flushList();
      var p = document.createElement('p');
      p.textContent = line;
      fragment.appendChild(p);
    });

    flushList();
    policyBody.innerHTML = '';
    policyBody.appendChild(fragment);
  }

  contentWrap.appendChild(policyContainer);

  var notice = document.createElement('aside');
  notice.className = 'jam-terms-policy__notice';
  notice.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6"></path><circle cx="12" cy="7" r="1" fill="currentColor" stroke="none"></circle></svg>' +
    '<p>These terms should be read together with the Shipping Policy, Return &amp; Refund Policy, Privacy Policy and any other applicable store policies.</p>';

  var help = document.createElement('aside');
  help.className = 'jam-terms-policy__help';
  help.innerHTML =
    '<div><h2>Questions about these terms?</h2><p>Use the Contact Us page if you need help understanding a store-policy or order-related matter.</p></div>' +
    '<a href="/pages/contact-us">Contact Us <span aria-hidden="true">&rarr;</span></a>';

  inner.appendChild(hero);
  inner.appendChild(highlights);
  inner.appendChild(contentWrap);
  inner.appendChild(notice);
  inner.appendChild(help);
  shell.appendChild(inner);

  main.insertBefore(shell, main.firstChild);
})();

/* JAM PROFESSIONAL PAYMENT POLICY PAGE */
(function () {
  if (window.location.pathname !== '/pages/payment-information') return;

  var mainPage = document.querySelector('.main-page');
  var rte = mainPage && mainPage.querySelector('.rte');
  if (!mainPage || !rte || mainPage.closest('.jam-payment-policy')) return;

  var main = mainPage.parentElement;
  if (!main) return;

  var shell = document.createElement('section');
  shell.className = 'jam-payment-policy';

  var inner = document.createElement('div');
  inner.className = 'container';

  var hero = document.createElement('header');
  hero.className = 'jam-payment-policy__hero';
  hero.innerHTML =
    '<span class="jam-payment-policy__eyebrow">Payments &amp; Checkout</span>' +
    '<h1>Payment Policy</h1>' +
    '<p>Review the accepted payment methods, payment verification requirements and processing information that applies before an order is prepared for shipment.</p>';

  var highlights = document.createElement('div');
  highlights.className = 'jam-payment-policy__highlights';
  highlights.innerHTML =
    '<article class="jam-payment-policy__highlight">' +
      '<span class="jam-payment-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="3" y="5" width="18" height="14" rx="2"></rect><path d="M3 10h18"></path></svg></span>' +
      '<h2>PayPal</h2><p>PayPal is listed as an accepted payment method in the store payment policy.</p>' +
    '</article>' +
    '<article class="jam-payment-policy__highlight">' +
      '<span class="jam-payment-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M3 8 12 3l9 5v2H3V8Z"></path><path d="M5 10v7M9 10v7M15 10v7M19 10v7M3 19h18"></path></svg></span>' +
      '<h2>Bank Transfer</h2><p>International Bank Transfer (TT/Wire Transfer) is also described in the current policy.</p>' +
    '</article>' +
    '<article class="jam-payment-policy__highlight">' +
      '<span class="jam-payment-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M12 3 20 6v6c0 5-3.2 9.1-8 11-4.8-1.9-8-6-8-11V6l8-3Z"></path><path d="m8.5 12 2.2 2.2 4.8-5"></path></svg></span>' +
      '<h2>Payment Verification</h2><p>Payments must be completed and verified before an order is processed and shipped.</p>' +
    '</article>' +
    '<article class="jam-payment-policy__highlight">' +
      '<span class="jam-payment-policy__highlight-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M4 4h16v16H4z"></path><path d="M8 9h8M8 13h8M8 17h5"></path></svg></span>' +
      '<h2>Order Processing</h2><p>Review the full policy for the conditions that apply before payment is cleared and an order proceeds.</p>' +
    '</article>';

  var contentWrap = document.createElement('div');
  contentWrap.className = 'jam-payment-policy__content';

  var card = document.createElement('div');
  card.className = 'jam-payment-policy__card';
  card.appendChild(rte);
  contentWrap.appendChild(card);

  var note = document.createElement('aside');
  note.className = 'jam-payment-policy__note';
  note.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6"></path><circle cx="12" cy="7" r="1" fill="currentColor" stroke="none"></circle></svg>' +
    '<p>Payment-method availability can depend on the checkout configuration and the payment options currently enabled for the store. Please review the full policy and checkout before completing payment.</p>';

  var help = document.createElement('aside');
  help.className = 'jam-payment-policy__help';
  help.innerHTML =
    '<div><h2>Need help with a payment question?</h2><p>Use the Contact Us page if you need assistance with a payment, order or checkout question.</p></div>' +
    '<a href="/pages/contact-us">Contact Us <span aria-hidden="true">&rarr;</span></a>';

  inner.appendChild(hero);
  inner.appendChild(highlights);
  inner.appendChild(contentWrap);
  inner.appendChild(note);
  inner.appendChild(help);
  shell.appendChild(inner);

  main.insertBefore(shell, main.firstChild);
  mainPage.style.display = 'none';
})();
